import type { IGraphics } from '../../rendering/IRenderer';
import { seededRand } from '../../effects/atmosphere/atmospherePlacement';

export interface HarborBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}
export interface HarborMaterialStyle {
  readonly seed: number;
  readonly palette: Readonly<{
    waterTop: number; waterDeep: number; waterLight: number;
    wood: number; woodShade: number; woodEdge: number;
    stone: number; stoneEdge: number; iron: number;
    rope: number; amber: number; hull: number; cabin: number;
  }>;
  /** Distances are local pixels from the bounds' top-left, independent of any map. */
  readonly dressing: Readonly<{
    deckHeight: number;
    abutmentWidth: number;
    pileOffsets: readonly number[];
    reflectionCount?: number;
    lampOffset?: number;
    ferry?: HarborBounds;
  }>;
}
type HarborGraphics = Pick<IGraphics, 'fillRect' | 'fillPolygon' | 'fillVerticalGradientRect'>;
const finite = (value: number | undefined, fallback: number) => Number.isFinite(value) ? value! : fallback;

/** A draw-once quay cutaway, reusing its caller's Graphics and single gradient. */
export function drawHarborMaterial(g: HarborGraphics, b: HarborBounds, style: HarborMaterialStyle): boolean {
  const { x, y, width, height } = b;
  if (![x, y, width, height, x + width, y + height].every(Number.isFinite) || width <= 0 || height <= 0) return false;
  const p = style.palette, d = style.dressing, seed = finite(style.seed, 0);
  const deck = Math.max(0, Math.min(height, finite(d.deckHeight, 28)));
  const abutment = Math.max(0, Math.min(width / 3, finite(d.abutmentWidth, 60)));
  const waterHeight = height - deck, innerWidth = width - abutment * 2;
  if (waterHeight > 0) {
    if (g.fillVerticalGradientRect) g.fillVerticalGradientRect(x, y + deck, width, waterHeight, p.waterTop, p.waterDeep, 1);
    else g.fillRect(x, y + deck, width, waterHeight, p.waterTop, 1);
    const count = Math.max(0, Math.min(24, Math.floor(finite(d.reflectionCount, 24))));
    const hasLamp = typeof d.lampOffset === 'number' && Number.isFinite(d.lampOffset);
    const warmCount = hasLamp ? Math.min(4, count) : 0;
    for (let i = 0; i < count; i++) {
      const warm = i >= count - warmCount;
      const rand = seededRand(seed + i * 19);
      const w = Math.min(innerWidth * 0.22, warm ? 14 + (i % 4) * 8 : 18 + rand * 68);
      const h = Math.min(waterHeight * 0.02, warm ? 2 : 1.5);
      const localX = warm ? finite(d.lampOffset, width / 2) - w / 2 + (rand - 0.5) * 12
        : abutment + seededRand(seed + i * 19 + 1) * (innerWidth - w);
      const left = Math.max(abutment, Math.min(width - abutment - w, localX));
      const top = deck + (waterHeight - h) * (warm ? 0.13 + (i % 4) * 0.085 : 0.025 + rand * 0.9);
      g.fillRect(x + left, y + top, w, h, warm ? p.amber : p.waterLight, warm ? 0.28 - (i % 4) * 0.04 : 0.1 + rand * 0.15);
    }
    const pileWidth = Math.min(16, innerWidth * 0.04);
    const drop = Math.min(216, waterHeight * 0.7);
    for (const offset of d.pileOffsets.slice(0, 8)) {
      if (!Number.isFinite(offset) || offset - pileWidth / 2 < abutment || offset + pileWidth / 2 > width - abutment) continue;
      const left = x + offset - pileWidth / 2, top = y + deck;
      g.fillRect(left, top, pileWidth, drop, p.woodShade, 1);
      g.fillRect(left + pileWidth * 0.18, top, pileWidth * 0.26, drop, p.wood, 0.85);
      g.fillRect(left, top + drop * 0.72, pileWidth, drop * 0.28, p.waterDeep, 0.65);
      g.fillRect(left, top + drop * 0.18, pileWidth, Math.min(5, drop * 0.04), p.iron, 1);
      g.fillRect(left, top + drop * 0.56, pileWidth, Math.min(5, drop * 0.04), p.iron, 1);
      const reach = Math.min(32, offset - abutment, width - abutment - offset);
      const braceWidth = Math.min(6, reach * 0.2);
      for (const direction of [-1, 1]) g.fillPolygon([
        { x: x + offset, y: top + Math.min(48, drop * 0.3) },
        { x: x + offset + direction * reach, y: top },
        { x: x + offset + direction * (reach - braceWidth), y: top },
        { x: x + offset, y: top + Math.min(35, drop * 0.22) },
      ], p.wood, 0.9);
    }
    drawFerry(g, b, style, deck, abutment);
    // Solid abutments join neighboring ground; only the center reads as open water.
    if (abutment > 0) for (const left of [x, x + width - abutment]) {
      g.fillRect(left, y + deck, abutment, waterHeight, p.stone, 1);
      g.fillRect(left, y + deck, Math.min(3, abutment), waterHeight, p.stoneEdge, 0.38);
      for (let row = 0; row < 4; row++) {
        const top = deck + row * 45, h = Math.min(43, height - top);
        if (h <= 0) continue;
        g.fillRect(left, y + top, abutment, Math.min(2, h), p.iron, 0.65);
        g.fillRect(left + abutment * (row % 2 ? 0.3 : 0.68), y + top, Math.min(2, abutment * 0.2), h, p.iron, 0.45);
      }
    }
  }
  if (deck > 0) {
    g.fillRect(x, y, width, deck, p.wood, 1);
    g.fillRect(x, y, width, Math.min(2, deck), p.woodEdge, 0.95);
    g.fillRect(x, y + deck * 0.7, width, deck * 0.3, p.woodShade, 1);
    const plankWidth = width / 18;
    for (let i = 0; i < 18; i++) {
      if (i > 0) g.fillRect(x + i * plankWidth, y + deck * 0.08, Math.min(1.5, plankWidth * 0.1), deck * 0.72, p.woodShade, 0.65);
      g.fillRect(x + (i + 0.2) * plankWidth, y + deck * 0.45, plankWidth * 0.55, Math.min(1, deck * 0.05), p.woodEdge, 0.35);
    }
  }
  return true;
}

function drawFerry(g: HarborGraphics, b: HarborBounds, style: HarborMaterialStyle, deck: number, abutment: number): void {
  const f = style.dressing.ferry;
  if (!f || ![f.x, f.y, f.width, f.height].every(Number.isFinite) || f.width <= 0 || f.height <= 0
    || f.x < abutment || f.x + f.width > b.width - abutment || f.y < deck || f.y + f.height > b.height) return;
  const p = style.palette, x = (n: number) => b.x + f.x + n * f.width, y = (n: number) => b.y + f.y + n * f.height;
  const polygon = (points: ReadonlyArray<readonly [number, number]>, color: number, alpha = 1) =>
    g.fillPolygon(points.map(([px, py]) => ({ x: x(px), y: y(py) })), color, alpha);
  const rect = (px: number, py: number, w: number, h: number, color: number, alpha = 1) =>
    g.fillRect(x(px), y(py), w * f.width, h * f.height, color, alpha);
  // Rope ends below the walking deck: visual mooring, never a suggested gangway.
  g.fillPolygon([{ x: b.x + b.width - abutment, y: b.y + deck }, { x: x(0.91), y: y(0.61) },
    { x: x(0.90), y: y(0.61) }, { x: b.x + b.width - abutment - Math.min(2, b.width * 0.02), y: b.y + deck }], p.rope, 0.65);
  polygon([[0, 0.61], [1, 0.57], [0.89, 0.91], [0.23, 1], [0.08, 0.88]], p.hull);
  polygon([[0.05, 0.65], [0.96, 0.62], [0.94, 0.70], [0.1, 0.74]], p.woodEdge, 0.75);
  polygon([[0.14, 0.83], [0.92, 0.78], [0.89, 0.91], [0.23, 1]], p.woodShade, 0.6);
  rect(0.28, 0.17, 0.4, 0.44, p.cabin);
  polygon([[0.24, 0.19], [0.31, 0.04], [0.63, 0.04], [0.73, 0.19]], p.iron);
  rect(0.57, 0, 0.05, 0.1, p.iron);
  for (const wx of [0.33, 0.47]) {
    rect(wx, 0.25, 0.1, 0.16, p.amber, 0.85);
    rect(wx + 0.045, 0.25, 0.012, 0.16, p.woodShade);
  }
  rect(0.58, 0.35, 0.06, 0.26, p.woodShade);
  rect(0.11, 0.48, 0.012, 0.15, p.rope, 0.8);
  rect(0.85, 0.45, 0.012, 0.14, p.rope, 0.8);
  rect(0.11, 0.48, 0.74, 0.018, p.rope, 0.8);
  rect(0.19, 0.72, 0.045, 0.15, p.iron);
  rect(0.75, 0.68, 0.045, 0.15, p.iron);
}
